/**
 * The participant's page.
 *
 * The screen is never advanced by a transition. It is recomputed from
 * `screenFor(state, status)` after every fetch, so a phone that was asleep
 * through the whole event arrives at the right screen by asking rather than by
 * having followed each step — which is the only way this can be right for
 * somebody who closed the tab after the first stage and came back hours later.
 */
import {
  EMAIL_COPY,
  ERROR_COPY,
  HANDOFF_COPY,
  PRIVACY_NOTE,
  PRIVACY_NOTE_WITH_EMAIL,
  SCREEN_COPY,
  SWAY_INVITATION,
  SWAY_SCREEN_COPY,
} from '../shared/copy';
import { statementOrder } from '../shared/order';
import {
  screenFor,
  shouldPoll,
  swayOffer,
  type MessageScreen,
  type Screen,
} from '../shared/screen';
import type { ParticipantStatus, PublicState } from '../shared/types';
import { validateEmail } from '../shared/validation';
import { ApiFailure, api } from './api';
import { must, setText, show } from './dom';
import { resolveParticipantId } from './identity';
import { markMissing, readAnswers, renderQuestions, type QuestionSpec } from './questions';
import { browserIdStore, clearDraft, readDraft, writeDraft } from './storage';

const POLL_FAST_MS = 10_000;
const POLL_SLOW_MS = 30_000;
const POLL_MAX_MS = 60_000;
/** How long a screen may sit unchanged in front of a waking phone before polling eases off. */
const SETTLE_AFTER_MS = 3 * 60_000;

const participantId = resolveParticipantId(browserIdStore());

let state: PublicState | null = null;
let status: ParticipantStatus | null = null;
let currentScreen: Screen | null = null;
/** Which screen and instrument the form in the DOM was built for, so it is not rebuilt under somebody. */
let builtForm: { screen: Screen; version: number } | null = null;
let pollTimer: ReturnType<typeof setTimeout> | undefined;
let pollDelay = POLL_FAST_MS;
let unchangedSince = Date.now();
let submitting = false;
/**
 * The participant pressed "Submit without joining Sway" on the nag. Cleared
 * the moment they type in the field or the form is rebuilt, so the choice is
 * for this press and not for the evening.
 */
let submitWithoutEmail = false;
/** Writes the current form to the draft; rebuilt with the form so it knows the specs. */
let saveDraft: (() => void) | null = null;
let handingOff = false;
/** The post form is up but the participant chose the discussion over the questions. */
let skippingPost = false;

const elements = {
  loading: must<HTMLElement>('#screen-loading'),
  message: must<HTMLElement>('#screen-message'),
  messageHeading: must<HTMLElement>('#message-heading'),
  messageBody: must<HTMLElement>('#message-body'),
  messageLink: must<HTMLAnchorElement>('#message-link'),
  form: must<HTMLElement>('#screen-form'),
  formHeading: must<HTMLElement>('#form-heading'),
  formIntro: must<HTMLElement>('#form-intro'),
  formElement: must<HTMLFormElement>('#survey-form'),
  questions: must<HTMLElement>('#questions'),
  formError: must<HTMLElement>('#form-error'),
  submit: must<HTMLButtonElement>('#submit'),
  privacy: must<HTMLElement>('#privacy-note'),
  connection: must<HTMLElement>('#connection'),
  emailField: must<HTMLElement>('#email-field'),
  emailInput: must<HTMLInputElement>('#email'),
  emailHint: must<HTMLElement>('#email-hint'),
  emailError: must<HTMLElement>('#email-error'),
  emailNag: must<HTMLElement>('#email-nag'),
  emailNagText: must<HTMLElement>('#email-nag-text'),
  emailNagAdd: must<HTMLButtonElement>('#email-nag-add'),
  emailNagSkip: must<HTMLButtonElement>('#email-nag-skip'),
  skipToSway: must<HTMLButtonElement>('#skip-to-sway'),
  backToQuestions: must<HTMLButtonElement>('#back-to-questions'),
  handoffForm: must<HTMLFormElement>('#handoff-form'),
  handoffEmailField: must<HTMLElement>('#handoff-email-field'),
  handoffEmailInput: must<HTMLInputElement>('#handoff-email'),
  handoffEmailHint: must<HTMLElement>('#handoff-email-hint'),
  handoffStatus: must<HTMLElement>('#handoff-status'),
  handoffError: must<HTMLElement>('#handoff-error'),
  handoffButton: must<HTMLButtonElement>('#handoff-button'),
  handoffFallback: must<HTMLAnchorElement>('#handoff-fallback'),
};

function questionSpecs(screen: Screen, config: PublicState['config']): QuestionSpec[] {
  const specs: QuestionSpec[] = config.statements.map((text, index) => ({
    key: `q${index + 1}`,
    text,
  }));
  if (screen === 'post-form' && config.postItem !== '') {
    specs.push({ key: 'extra', text: config.postItem });
  }
  return specs;
}

/**
 * The sequence the fieldsets are laid out in.
 *
 * The statements are ordered by {@link statementOrder}, which derives the same
 * permutation from this device's identifier every time — so a participant sees
 * one order before the event and the same order after it, and the change score
 * the survey exists to compute is not a comparison across two different
 * presentations. A reload mid-form does not reshuffle the page under anybody
 * for the same reason.
 *
 * The post-only item, when there is one, is always last. It is asked once, it
 * is not one of the repeated statements, and its answer is a column of its own.
 */
function displayOrder(config: PublicState['config'], specs: QuestionSpec[]): number[] {
  const statements = statementOrder(participantId, config.statements.length, config.randomizeOrder);
  const extra = specs.length > config.statements.length ? [config.statements.length] : [];
  return [...statements, ...extra];
}

function draftStage(screen: Screen): 'pre' | 'post' {
  return screen === 'post-form' ? 'post' : 'pre';
}

/**
 * What a message screen says, given whether it can send people to Sway.
 *
 * The survey's own wording stands when there is nowhere to go. Once the
 * discussion is open, the screens that turn people away from the questions
 * say so and offer it instead — a latecomer is late for the questions, not
 * for the evening.
 */
function messageCopy(screen: MessageScreen, offered: boolean): { heading: string; body: string } {
  if (screen === 'post-skip') return SWAY_SCREEN_COPY['post-skip'];
  if (offered && (screen === 'pre-missed' || screen === 'post-closed')) {
    return SWAY_SCREEN_COPY[screen];
  }
  if (offered && screen === 'done') {
    // The done screen is where somebody who submitted without an address
    // ends up, so it makes the case for the discussion rather than merely
    // closing the survey.
    return {
      heading: SWAY_INVITATION.heading,
      body: `${SCREEN_COPY.done.body} ${SWAY_INVITATION.body}`,
    };
  }
  const copy = SCREEN_COPY[screen];
  // Not open yet, but it will be: say so, because the page will change under
  // them and a change with no warning reads as a fault.
  const willBe =
    state !== null && (state.config.emailStage !== 'none' || state.config.swayUrl !== '');
  if (screen === 'pre-missed' && willBe) {
    return { heading: copy.heading, body: `${copy.body} ${SWAY_SCREEN_COPY.waiting}` };
  }
  return copy;
}

function renderMessage(screen: MessageScreen): void {
  const offer = state === null ? 'none' : swayOffer(screen, state);
  const copy = messageCopy(screen, offer !== 'none');
  setText(elements.messageHeading, copy.heading);
  setText(elements.messageBody, copy.body);

  // The plain link is shown as a link rather than an automatic redirect,
  // because somebody arriving here may already have been redirected once and
  // bouncing them again would be disorienting.
  const destination = state?.config.swayUrl ?? '';
  if (destination !== '') elements.messageLink.href = destination;
  show(elements.messageLink, offer === 'link');

  // With accounts switched on, the way forward is a button that asks Sway
  // for a sign-in link — the same thing the post form does on its own, for
  // the phone that lost that redirect, came back later, or never had a form.
  // The address field appears only when there is none on file.
  if (!handingOff) {
    show(elements.handoffStatus, false);
    show(elements.handoffError, false);
    show(elements.handoffFallback, false);
    show(elements.handoffEmailField, offer === 'handoff' && !(status?.emailOnFile ?? false));
  }
  setText(elements.handoffEmailHint, HANDOFF_COPY.emailHint);
  show(elements.handoffForm, offer === 'handoff');
  show(elements.backToQuestions, screen === 'post-skip');

  show(elements.loading, false);
  show(elements.form, false);
  show(elements.message, true);
}

function renderForm(screen: 'pre-form' | 'post-form', config: PublicState['config']): void {
  const copy = SCREEN_COPY[screen];
  setText(elements.formHeading, copy.heading);
  setText(elements.formIntro, copy.body);
  elements.formIntro.classList.toggle('callout', screen === 'post-form');

  const stage = draftStage(screen);
  // Asked on the form the organizer chose — and again on the post form for
  // somebody who was asked before and left it blank, since that form is the
  // last one before the discussion they would otherwise miss.
  const asksEmail =
    config.emailStage === stage ||
    (stage === 'post' && config.emailStage !== 'none' && !(status?.emailOnFile ?? false));
  setText(elements.privacy, asksEmail ? PRIVACY_NOTE_WITH_EMAIL : PRIVACY_NOTE);
  setText(elements.emailHint, stage === 'pre' ? EMAIL_COPY.hintPre : EMAIL_COPY.hintPost);
  setText(elements.emailNagText, EMAIL_COPY.nag);
  setText(elements.emailNagAdd, EMAIL_COPY.nagAdd);
  setText(elements.emailNagSkip, EMAIL_COPY.nagSkip);
  show(elements.emailField, asksEmail);

  // Rebuilding the fieldsets would clear every radio the participant has
  // already chosen, so it happens only when the form is genuinely a different
  // one. A poll that changes nothing must not disturb somebody mid-answer.
  if (builtForm?.screen !== screen || builtForm.version !== config.version) {
    const specs = questionSpecs(screen, config);
    const draft = readDraft(stage, config.version);
    saveDraft = () => {
      writeDraft(stage, {
        instrumentVersion: config.version,
        answers: readAnswers(elements.questions, specs),
        email: elements.emailInput.value,
      });
    };
    renderQuestions(elements.questions, {
      specs,
      labels: config.scaleLabels,
      initial: specs.map((_, index) => draft?.answers[index] ?? null),
      order: displayOrder(config, specs),
      onChange: () => {
        // The per-question message clears itself; this is the summary above
        // the button, which would otherwise still be asking for an answer that
        // has just been given.
        show(elements.formError, false);
        saveDraft?.();
      },
    });
    elements.emailInput.value = draft?.email ?? '';
    show(elements.emailError, false);
    show(elements.emailNag, false);
    submitWithoutEmail = false;
    builtForm = { screen, version: config.version };
  }

  show(elements.formError, false);
  elements.submit.disabled = false;
  setText(elements.submit, 'Submit');
  // The discussion is for everyone who was in the room. Somebody on the post
  // form who would rather not answer is still sent on, quietly, below the
  // questions rather than beside the Submit button.
  setText(elements.skipToSway, HANDOFF_COPY.skip);
  show(
    elements.skipToSway,
    screen === 'post-form' && state !== null && swayOffer('post-skip', state) !== 'none',
  );
  show(elements.loading, false);
  show(elements.message, false);
  show(elements.form, true);
}

function render(): void {
  if (state === null || status === null) return;
  const screen = screenFor(state, status);
  if (screen !== currentScreen) {
    currentScreen = screen;
    unchangedSince = Date.now();
    pollDelay = POLL_FAST_MS;
  }
  if (screen === 'post-form' && skippingPost) {
    // The form stays built underneath, so "back" finds the answers where
    // they were left.
    renderMessage('post-skip');
  } else if (screen === 'pre-form' || screen === 'post-form') {
    skippingPost = false;
    renderForm(screen, state.config);
  } else {
    // Emptying the container matters rather than merely hiding it. A submitted
    // form left in the DOM is a copy of the answers, sitting behind a `hidden`
    // attribute that anybody who picks up the phone can remove — and sealing
    // them is the point of the whole design. The address goes with them: it is
    // not sealed, but it is not for the next person to pick up the phone.
    builtForm = null;
    saveDraft = null;
    submitWithoutEmail = false;
    skippingPost = false;
    elements.questions.replaceChildren();
    elements.emailInput.value = '';
    renderMessage(screen);
  }
  schedulePoll();
}

function showConnectionTrouble(visible: boolean): void {
  setText(elements.connection, ERROR_COPY.offline);
  show(elements.connection, visible);
}

async function refresh(): Promise<void> {
  const [nextState, nextStatus] = await Promise.all([api.state(), api.status(participantId)]);
  state = nextState;
  status = nextStatus;
  // Before `render`, which schedules the next poll from this value. Set after
  // it instead — which is where it used to be — a page that had backed off to
  // a minute during a wifi drop would answer the first successful request by
  // waiting another minute, and somebody would sit on "Not open yet" long
  // after the stage opened.
  pollDelay = POLL_FAST_MS;
  showConnectionTrouble(false);
  render();
}

function schedulePoll(): void {
  clearTimeout(pollTimer);
  if (currentScreen === null || !shouldPoll(currentScreen)) return;
  if (document.visibilityState !== 'visible') return;
  // A page left face-up on a table need not ask every ten seconds for an hour.
  // Anything that changes the screen, and any return from the background,
  // resets this.
  const settled = Date.now() - unchangedSince > SETTLE_AFTER_MS;
  const delay = Math.min(settled ? Math.max(pollDelay, POLL_SLOW_MS) : pollDelay, POLL_MAX_MS);
  pollTimer = setTimeout(() => {
    void poll();
  }, delay);
}

async function poll(): Promise<void> {
  if (submitting || handingOff) {
    schedulePoll();
    return;
  }
  try {
    await refresh();
  } catch {
    // Backing off doubles the gap each time rather than hammering a server
    // that is already struggling, and the banner tells the participant the
    // page has not given up.
    pollDelay = Math.min(pollDelay * 2, POLL_MAX_MS);
    showConnectionTrouble(true);
    schedulePoll();
  }
}

function reportSubmitProblem(message: string): void {
  setText(elements.formError, message);
  show(elements.formError, true);
  elements.formError.scrollIntoView({ block: 'nearest' });
}

async function submit(event: SubmitEvent): Promise<void> {
  event.preventDefault();
  await performSubmit();
}

async function performSubmit(): Promise<void> {
  if (state === null || currentScreen === null || submitting) return;
  if (currentScreen !== 'pre-form' && currentScreen !== 'post-form') return;

  const config = state.config;
  const specs = questionSpecs(currentScreen, config);
  const answers = readAnswers(elements.questions, specs);
  const missing = markMissing(elements.questions, specs, answers);
  if (missing !== null) {
    reportSubmitProblem(ERROR_COPY.unanswered);
    missing.scrollIntoView({ block: 'center', behavior: 'smooth' });
    missing.querySelector<HTMLInputElement>('input')?.focus();
    return;
  }

  // Every question is answered, or `markMissing` would have returned above.
  // Narrowing rather than casting keeps that an argument the compiler checks
  // instead of one this comment makes.
  const chosen: number[] = [];
  for (const value of answers) {
    if (typeof value !== 'number') return;
    chosen.push(value);
  }
  // The statements come first and the post-only item, when there is one, last.
  const statementAnswers = chosen.slice(0, config.statements.length);
  const postItemAnswer = chosen[config.statements.length] ?? null;

  const stage = draftStage(currentScreen);

  // The address, when the field is up. A typo is caught by the field it was
  // typed into rather than by a sentence from the server a round trip later.
  // An empty field is not a typo: the answers go through without it, but
  // only after the nag has been shown and its quiet button pressed — leaving
  // without an address is a choice, never the default.
  let email: string | undefined;
  if (!elements.emailField.hidden) {
    if (elements.emailInput.value.trim() === '') {
      if (!submitWithoutEmail) {
        show(elements.emailNag, true);
        elements.emailNag.scrollIntoView({ block: 'nearest' });
        elements.emailNagAdd.focus();
        return;
      }
    } else {
      const typed = validateEmail(elements.emailInput.value);
      if (!typed.ok) {
        setText(elements.emailError, typed.error);
        show(elements.emailError, true);
        reportSubmitProblem(typed.error);
        elements.emailInput.focus();
        return;
      }
      email = typed.value;
    }
  }

  submitting = true;
  clearTimeout(pollTimer);
  elements.submit.disabled = true;
  setText(elements.submit, 'Saving…');
  show(elements.formError, false);

  try {
    const accepted =
      currentScreen === 'pre-form'
        ? await api.submitPre({
            participantId,
            answers: statementAnswers,
            instrumentVersion: config.version,
            ...(email === undefined ? {} : { email }),
          })
        : await api.submitPost({
            participantId,
            answers: statementAnswers,
            postItemAnswer,
            instrumentVersion: config.version,
            ...(email === undefined ? {} : { email }),
          });

    clearDraft(stage);
    const emailOnFile = email !== undefined || (status?.emailOnFile ?? false);
    status =
      stage === 'pre'
        ? { preSubmitted: true, postSubmitted: status?.postSubmitted ?? false, emailOnFile }
        : { preSubmitted: true, postSubmitted: true, emailOnFile };

    if (stage === 'post' && accepted.swayHandoff) {
      // The answers are on file; now the account. The done screen goes up
      // underneath so that a Sway that cannot be reached leaves a button to
      // press rather than a form that appears to have swallowed the answers.
      submitting = false;
      render();
      await runHandoff(null);
      return;
    }
    if (stage === 'post' && accepted.swayUrl.startsWith('https://')) {
      // `replace`, not `assign`: the survey is finished, and leaving it in the
      // history would put a completed form one Back gesture away.
      location.replace(accepted.swayUrl);
      return;
    }
    submitting = false;
    render();
  } catch (cause) {
    submitting = false;
    elements.submit.disabled = false;
    setText(elements.submit, 'Submit');

    if (!(cause instanceof ApiFailure)) {
      reportSubmitProblem(ERROR_COPY.submitFailed);
      schedulePoll();
      return;
    }
    if (cause.code === 'offline') {
      reportSubmitProblem(ERROR_COPY.submitFailed);
      schedulePoll();
      return;
    }

    // The server has told us the world moved: the stage closed, the questions
    // changed, or this participant is not eligible. Re-reading the world and
    // re-rendering puts the right screen up; the message says why it changed.
    reportSubmitProblem(cause.message);
    try {
      await refresh();
    } catch {
      showConnectionTrouble(true);
    }
  }
}

/** Failures worth a second try without asking: Sway busy, Sway down, or no network. */
function handoffRetryable(cause: unknown): boolean {
  if (!(cause instanceof ApiFailure)) return true;
  return cause.code === 'offline' || cause.code === 'sway_unavailable' || cause.status >= 500;
}

const HANDOFF_RETRIES = 3;
const HANDOFF_RETRY_MS = 2_000;

function wait(ms: number): Promise<void> {
  return new Promise((wake) => setTimeout(wake, ms));
}

/**
 * Ask Sway for a sign-in link and go there.
 *
 * Three quiet retries with a doubling gap before the participant is told,
 * because the likeliest reason this fails at an event is the venue's network
 * or Sway's rate limit, and both clear in seconds. What the participant sees
 * throughout is one sentence and no button to press. Only after that does it
 * become their problem — with the button, and the plain link as a way through
 * if the organizer set one.
 */
async function runHandoff(email: string | null): Promise<void> {
  if (handingOff) return;
  handingOff = true;
  show(elements.handoffError, false);
  show(elements.handoffFallback, false);
  setText(elements.handoffStatus, HANDOFF_COPY.working);
  show(elements.handoffStatus, true);
  elements.handoffButton.disabled = true;

  try {
    for (let attempt = 0; ; attempt++) {
      try {
        const accepted = await api.handoff({
          participantId,
          ...(email === null ? {} : { email }),
        });
        if (status !== null) status = { ...status, emailOnFile: true };
        setText(elements.handoffStatus, HANDOFF_COPY.ready);
        // `replace`, not `assign`: the survey is finished, and leaving it in
        // the history would put a completed form one Back gesture away.
        location.replace(accepted.redirectUrl);
        return;
      } catch (cause) {
        if (handoffRetryable(cause) && attempt < HANDOFF_RETRIES) {
          await wait(HANDOFF_RETRY_MS * 2 ** attempt);
          continue;
        }
        show(elements.handoffStatus, false);
        if (cause instanceof ApiFailure && cause.code === 'email_required') {
          show(elements.handoffEmailField, true);
          setText(elements.handoffError, HANDOFF_COPY.emailNeeded);
          show(elements.handoffError, true);
          elements.handoffEmailInput.focus();
          return;
        }
        if (cause instanceof ApiFailure && cause.code === 'sway_refused') {
          // Sway's own sentence says what was wrong with the address, and
          // the field comes up so it can be corrected.
          show(elements.handoffEmailField, true);
          setText(elements.handoffError, `${HANDOFF_COPY.refused} ${cause.message}`);
          show(elements.handoffError, true);
          elements.handoffEmailInput.focus();
          return;
        }
        setText(
          elements.handoffError,
          cause instanceof ApiFailure && !handoffRetryable(cause)
            ? cause.message
            : HANDOFF_COPY.failed,
        );
        show(elements.handoffError, true);
        const fallback = state?.config.swayUrl ?? '';
        if (fallback.startsWith('https://')) {
          elements.handoffFallback.href = fallback;
          show(elements.handoffFallback, true);
        }
        return;
      }
    }
  } finally {
    handingOff = false;
    elements.handoffButton.disabled = false;
  }
}

function start(): void {
  elements.formElement.addEventListener('submit', (event) => {
    void submit(event);
  });

  elements.emailInput.addEventListener('input', () => {
    show(elements.emailError, false);
    show(elements.emailNag, false);
    submitWithoutEmail = false;
    saveDraft?.();
  });

  elements.emailNagAdd.addEventListener('click', () => {
    show(elements.emailNag, false);
    elements.emailInput.focus();
  });

  elements.emailNagSkip.addEventListener('click', () => {
    submitWithoutEmail = true;
    show(elements.emailNag, false);
    void performSubmit();
  });

  elements.skipToSway.addEventListener('click', () => {
    if (currentScreen !== 'post-form' || submitting) return;
    skippingPost = true;
    render();
  });

  elements.backToQuestions.addEventListener('click', () => {
    if (handingOff) return;
    skippingPost = false;
    render();
  });

  elements.handoffForm.addEventListener('submit', (event) => {
    event.preventDefault();
    if (currentScreen === null || handingOff) return;
    let email: string | null = null;
    if (!elements.handoffEmailField.hidden) {
      const typed = validateEmail(elements.handoffEmailInput.value);
      if (!typed.ok) {
        setText(
          elements.handoffError,
          elements.handoffEmailInput.value.trim() === '' ? EMAIL_COPY.missing : typed.error,
        );
        show(elements.handoffError, true);
        elements.handoffEmailInput.focus();
        return;
      }
      email = typed.value;
    }
    void runHandoff(email);
  });

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      clearTimeout(pollTimer);
      return;
    }
    // Somebody has just taken the phone out of their pocket. Whatever the
    // backoff had grown to, the right thing now is to ask immediately.
    pollDelay = POLL_FAST_MS;
    unchangedSince = Date.now();
    if (currentScreen !== null && shouldPoll(currentScreen)) void poll();
  });

  void (async () => {
    try {
      await refresh();
    } catch {
      showConnectionTrouble(true);
      // Nothing is known yet, so there is no screen to compute. Retrying on
      // the same backoff is what turns a phone that came up before the network
      // did into one that simply takes a moment.
      pollDelay = POLL_FAST_MS;
      pollTimer = setTimeout(() => {
        void retryFirstLoad();
      }, pollDelay);
    }
  })();
}

async function retryFirstLoad(): Promise<void> {
  try {
    await refresh();
  } catch {
    pollDelay = Math.min(pollDelay * 2, POLL_MAX_MS);
    pollTimer = setTimeout(() => {
      void retryFirstLoad();
    }, pollDelay);
  }
}

start();
